import { Logger } from '../../utils';
import { getFullUrl } from '../../utils/helpers-with-import-meta';

export class AudioManager {
  public static AUDIO_CONTEXT: AudioContext;

  public static GLOBAL_GAIN_NODE: GainNode;

  protected audioUrl: string;

  #mediaElement: HTMLAudioElement;

  private static audioList: HTMLAudioElement[] = [];

  private gainNode: GainNode;

  private static totalAudio: number = 0;

  private track: MediaElementAudioSourceNode;

  constructor(
    src: string,
    parentGainNode: GainNode,
    audioConfig: Partial<HTMLAudioElement> = {},
    gainOptions: Partial<GainOptions> = {}
  ) {
    // Use new Audio default has preload="auto"
    this.audioUrl = getFullUrl(src);

    const audio = AudioManager.audioList[AudioManager.totalAudio];
    audio.src = this.audioUrl;
    AudioManager.totalAudio += 1;

    this.#mediaElement = audio;

    /**
     * Event trigger order:
     * loadstart -> loadedmetadata -> loadeddata  -> canplay -> canplaythrough
     *
     * canplaythrough means the audio is ready to play without jerky (buffer all data?)
     */

    this.gainNode = new GainNode(AudioManager.AUDIO_CONTEXT, gainOptions);

    this.updateAudioConfig(audioConfig);

    this.track = new MediaElementAudioSourceNode(AudioManager.AUDIO_CONTEXT, {
      mediaElement: this.mediaElement,
    });

    this.track
      .connect(this.gainNode)
      .connect(parentGainNode)
      .connect(AudioManager.GLOBAL_GAIN_NODE)
      .connect(AudioManager.AUDIO_CONTEXT.destination);
  }

  protected static init() {
    if (!AudioManager.AUDIO_CONTEXT) {
      AudioManager.AUDIO_CONTEXT = new AudioContext();
    }

    if (!AudioManager.GLOBAL_GAIN_NODE) {
      AudioManager.GLOBAL_GAIN_NODE = AudioManager.AUDIO_CONTEXT.createGain();
    }
  }

  /**
   * HTML5 Audio objects must be individually unlocked, so we have to unlock all in one single
   * user interaction.
   *
   * @see https://github.com/goldfire/howler.js/pull/1008
   */
  public static initAudio(total: number) {
    AudioManager.audioList = [];
    AudioManager.totalAudio = 0;
    for (let i = 0; i < total; i += 1) {
      const audio = new Audio();
      audio.crossOrigin = 'anonymous'; // IMPORTANT
      AudioManager.audioList.push(audio);
    }
  }

  public static setVolumeSmoothly(
    gainNode: GainNode,
    value: number,
    duration: number = 1000
  ) {
    gainNode.gain.linearRampToValueAtTime(
      gainNode.gain.value,
      AudioManager.AUDIO_CONTEXT.currentTime
    );
    gainNode.gain.linearRampToValueAtTime(
      value,
      AudioManager.AUDIO_CONTEXT.currentTime + duration / 1000
    );
  }

  public close() {
    this.pause();

    /**
     * Little track to clear the Android notification bar player
     * @see https://stackoverflow.com/a/40879593/18793431
     */
    this.mediaElement.src = '';
  }

  public async fadeIn(duration: number = 1000) {
    const isSuccess = await this.play();
    if (isSuccess) {
      this.gainNode.gain.value = 0;
      this.setVolume(1, duration);
    }
    return isSuccess;
  }

  public fadeOut(duration: number = 1000) {
    this.setVolume(0, duration);
    this.pause();
  }

  public pause() {
    this.mediaElement.pause();
  }

  public async play() {
    try {
      /**
       * We have to add this check otherwise will show below error:
       * The play() request was interrupted by a new load request.
       */
      if (this.mediaElement.src !== this.audioUrl) {
        // Once closed, the src will be empty, so we need to reset it
        this.mediaElement.src = this.audioUrl;
      }

      await this.mediaElement.play();
      return true;
    } catch (error: any) {
      Logger.error(error?.message || error);
      return false;
    }
  }

  public setVolume(value: number, duration: number = 1000) {
    AudioManager.setVolumeSmoothly(this.gainNode, value, duration);
  }

  public updateAudioConfig(audioConfig: Partial<HTMLAudioElement>) {
    const keys = Object.keys(audioConfig);
    for (let i = 0; i < keys.length; i += 1) {
      const key = keys[i] as keyof HTMLAudioElement;
      (this.mediaElement as any)[key] = audioConfig[key];
    }
  }

  get mediaElement() {
    return this.#mediaElement;
  }

  set mediaElement(value: HTMLAudioElement) {
    this.#mediaElement = value;
  }
}
